import { useMemo, useState } from "react";
import { ExpenseThisMonthCard } from "./ExpenseThisMonthCard";
import { ExpenseThisMonthInsightsPanel } from "./ExpenseThisMonthInsightsPanel";
import type { ExpenseThisMonthInsightsPanelProps } from "./ExpenseThisMonthInsightsPanel";
import { useExpenseThisMonth } from "../../hooks/useExpenseThisMonth";
import { useExpenseThisMonthInsights } from "../../hooks/useExpenseThisMonthInsights";

const DEFAULT_INSIGHTS: ExpenseThisMonthInsightsPanelProps["insights"] = [
  {
    title: "SPEND PACE",
    description:
      "Spend pace insights will appear once enough data is available for the selected period.",
    accentColor: "success",
  },
  {
    title: "BILLS LOGGED",
    description:
      "6 bills entered this month vs 5 at the same point in January.",
    accentColor: "neutral",
  },
];

export function ExpenseThisMonthSection() {
  const {
    title,
    subtext,
    amountThisWeek,
    amountThisMonth,
    subTotalThisWeek,
    taxTotalThisWeek,
    subTotalThisMonth,
    taxTotalThisMonth,
    amountLabel,
    badgePrimary,
    weekBadgeLabel,
    showEst,
    loading,
    error,
  } = useExpenseThisMonth();
  const [activePeriod, setActivePeriod] = useState<"month" | "week">("month");

  const insightsData = useExpenseThisMonthInsights({
    enabled: !loading && !error,
  });
  const insightsPanelLoading = loading || insightsData.loading;

  const insightsMonthLabel =
    subtext && subtext !== "—"
      ? subtext.split(" · ")[0] + " · Insights"
      : "Insights";

  const selectedAmount = useMemo(
    () => (activePeriod === "month" ? amountThisMonth : amountThisWeek),
    [activePeriod, amountThisMonth, amountThisWeek],
  );

  const selectedBreakup = useMemo(() => {
    if (activePeriod === "month") {
      return { subTotal: subTotalThisMonth, taxTotal: taxTotalThisMonth };
    }
    return { subTotal: subTotalThisWeek, taxTotal: taxTotalThisWeek };
  }, [
    activePeriod,
    subTotalThisMonth,
    taxTotalThisMonth,
    subTotalThisWeek,
    taxTotalThisWeek,
  ]);

  return (
    <div className="flex w-full flex-col items-start gap-4 rounded-lg bg-white px-6 py-6 shadow-sm">
      <div className="flex w-full gap-6 items-stretch">
        <ExpenseThisMonthCard
          title={title}
          subtext={subtext}
          amount={selectedAmount}
          subTotal={selectedBreakup.subTotal}
          taxTotal={selectedBreakup.taxTotal}
          amountLabel={amountLabel}
          badgePrimary={badgePrimary}
          badgeSecondary={weekBadgeLabel}
          showEst={showEst}
          loading={loading}
          activePeriod={activePeriod}
          onPeriodChange={setActivePeriod}
        />
        <ExpenseThisMonthInsightsPanel
          monthLabel={
            insightsData.error ? insightsMonthLabel : insightsData.monthLabel
          }
          insights={
            insightsData.error ? DEFAULT_INSIGHTS : insightsData.insights
          }
          loading={insightsPanelLoading}
        />
      </div>
    </div>
  );
}
